// @flow
import { FormBuilderContext } from 'pages/Contexts/FormBuilderContext'
import * as React from 'react'
import useBuilderQuestion from 'utils/FormBuilder/hooks/useBuilderQuestion'

import BuilderCalculationEditProps from './BuilderCalculationEditProps'
import BuilderCheckboxEditProps from './BuilderCheckboxEditProps'
import BuilderHandoutEditProps from './BuilderHandoutEditProps'
import BuilderMarkdownEditProps from './BuilderMarkdownEditProps'
import BuilderMarkerEditProps from './BuilderMarkerEditProps'
import BuilderMatrixEditProps from './BuilderMatrixEditProps'
import BuilderSignatureEditProps from './BuilderSignatureEditProps'
import BuilderSummaryEditProps from './BuilderSummaryEditProps'

type Props = {
  hasAssignValues: boolean,
}

function BuilderSideMenuEditQuestionProps({
  hasAssignValues,
}: Props): React.Node {
  const { selectedItem, setQuestion } = React.useContext(FormBuilderContext)

  const question = useBuilderQuestion(selectedItem.id)
  const onUpdateQuestionProps = React.useCallback(
    (newProps) => {
      setQuestion(selectedItem.id, {
        ...question,
        ...newProps,
      })
    },
    [question, selectedItem, setQuestion]
  )

  switch (question?.question_type?.question_type) {
    case 'file_upload':
      switch (question?.question_type?.question_subtype) {
        case 'marker':
          return (
            <BuilderMarkerEditProps
              onUpdateQuestionProps={onUpdateQuestionProps}
              question={question}
            />
          )
        case 'handout':
          return (
            <BuilderHandoutEditProps
              onUpdateQuestionProps={onUpdateQuestionProps}
              question={question}
            />
          )
        default:
          return null
      }
    case 'checkbox':
      switch (question?.question_type?.question_subtype) {
        case 'vertical_list':
          return (
            <BuilderCheckboxEditProps
              isVertical
              mayRequestDetails
              assignValues={hasAssignValues && question.assign_values}
              onUpdateQuestionProps={onUpdateQuestionProps}
              question={question}
              questionId={selectedItem.id}
            />
          )
        case 'horizontal_list':
          return (
            <BuilderCheckboxEditProps
              mayRequestDetails
              assignValues={hasAssignValues && question.assign_values}
              onUpdateQuestionProps={onUpdateQuestionProps}
              question={question}
              questionId={selectedItem.id}
            />
          )
        default:
          return null
      }
    case 'radio':
      switch (question?.question_type?.question_subtype) {
        case 'vertical_list':
          return (
            <BuilderCheckboxEditProps
              isVertical
              mayRequestDetails
              assignValues={hasAssignValues && question.assign_values}
              onUpdateQuestionProps={onUpdateQuestionProps}
              question={question}
              questionId={selectedItem.id}
            />
          )
        case 'horizontal_list':
          return (
            <BuilderCheckboxEditProps
              mayRequestDetails
              assignValues={hasAssignValues && question.assign_values}
              onUpdateQuestionProps={onUpdateQuestionProps}
              question={question}
              questionId={selectedItem.id}
            />
          )
        case 'dropdown':
        case 'dropdown_multiple':
          return (
            <BuilderCheckboxEditProps
              assignValues={hasAssignValues && question.assign_values}
              onUpdateQuestionProps={onUpdateQuestionProps}
              question={question}
              questionId={selectedItem.id}
            />
          )
        default:
          return null
      }
    case 'matrix':
      switch (question?.question_type?.question_subtype) {
        case 'radio':
        case 'checkbox':
        case 'open_ended':
          return (
            <BuilderMatrixEditProps
              assignValues={hasAssignValues && question.assign_values}
              onUpdateQuestionProps={onUpdateQuestionProps}
              questionColumns={question.question_columns}
              questionId={selectedItem.id}
              questionRows={question.question_rows}
            />
          )
        default:
          return null
      }
    case 'table':
      switch (question?.question_type?.question_subtype) {
        case 'open_ended':
          return (
            <BuilderMatrixEditProps
              isTable
              onUpdateQuestionProps={onUpdateQuestionProps}
              questionColumns={question.question_columns}
              questionId={selectedItem.id}
              questionRows={question.question_rows}
            />
          )
        default:
          return null
      }
    case 'display_only':
      switch (question?.question_type?.question_subtype) {
        case 'markdown':
          return <BuilderMarkdownEditProps />
        default:
          return null
      }
    case 'text':
      switch (question?.question_type?.question_subtype) {
        case 'calculation':
          return <BuilderCalculationEditProps questionId={selectedItem.id} />
        default:
          return null
      }
    case 'signature':
      return (
        <BuilderSignatureEditProps
          onUpdateQuestionProps={onUpdateQuestionProps}
          question={question}
        />
      )
    case 'summary':
      return (
        <BuilderSummaryEditProps
          onUpdateQuestionProps={onUpdateQuestionProps}
          question={question}
        />
      )
    default:
      return null
  }
}

export default BuilderSideMenuEditQuestionProps
